import { useState } from 'react'
import { ClipboardList, FileSpreadsheet, Loader2, Search, UploadCloud } from 'lucide-react'
import { InputDate } from '../../../componentesCompartilhados/form/InputDate'
import { FileUpload } from '../../../componentesCompartilhados/form/FileUpload'
import { Modal } from '../../../componentesCompartilhados/ui/Modal'
import {
  faturamentoService,
  type PlanilhaSuportePreviewApontamentoDto,
  type PlanilhaSuportePreviewDto,
  type PlanilhaSuportePreviewTicketConferenciaDto,
} from '../../projetos/services/faturamento'
import { useToast } from '../../../contexts/NotificationContext'

function formatarDataValidade(iso: string | null | undefined): string {
  if (iso == null || iso === '')
    return '—'
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString('pt-BR')
}

function ConteudoModalColaboradorApontamentos({
  nomeColaborador,
  conferenciaTickets,
  itens,
}: {
  nomeColaborador: string
  conferenciaTickets: PlanilhaSuportePreviewTicketConferenciaDto[]
  itens: PlanilhaSuportePreviewApontamentoDto[]
}) {
  return (
    <div className="space-y-6 max-h-[min(75vh,40rem)] overflow-y-auto pr-1">
      <div>
        <p className="text-sm font-medium text-gray-800">Conferência por ticket (antes da redistribuição)</p>
        <p className="mt-1 text-xs text-gray-500">
          Minutos atribuídos a partir do <strong>arquivo de faturamento</strong> (Id ticket) vs soma no banco, só para os
          tickets com apontamento deste colaborador. Linha com vários Ids reparte o minuto da linha entre os tickets.
        </p>
        {conferenciaTickets.length === 0 ? (
          <p className="mt-2 text-sm text-gray-600">Nenhum registro de conferência para estes tickets.</p>
        ) : (
          <div className="mt-2 overflow-x-auto rounded-lg border border-gray-200">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-left text-xs text-gray-600">
                <tr>
                  <th className="px-3 py-2 font-medium">Id ticket</th>
                  <th className="px-3 py-2 font-medium">Número</th>
                  <th className="px-3 py-2 text-right font-medium">Min. arquivo (planilha)</th>
                  <th className="px-3 py-2 text-right font-medium">Min. banco (apont.)</th>
                  <th className="px-3 py-2 text-right font-medium">Diferença</th>
                </tr>
              </thead>
              <tbody>
                {conferenciaTickets.map((t) => {
                  const d = t.minutosAtribuidosPlanilha - t.minutosSomaApontamentosBanco
                  const dTxt = `${d >= 0 ? '+' : '-'}${Math.abs(d).toLocaleString('pt-BR')}`
                  const dCls =
                    d === 0
                      ? 'text-gray-500'
                      : d > 0
                        ? 'text-amber-800'
                        : 'text-blue-800'
                  return (
                    <tr key={t.idTicket} className="border-t border-gray-100">
                      <td className="px-3 py-2 font-mono text-gray-900">{t.idTicket}</td>
                      <td className="px-3 py-2 text-gray-800">
                        {t.numeroTicket?.trim() ? t.numeroTicket : '—'}
                      </td>
                      <td className="px-3 py-2 text-right font-mono tabular-nums text-gray-900">
                        {t.minutosAtribuidosPlanilha.toLocaleString('pt-BR')}
                      </td>
                      <td className="px-3 py-2 text-right font-mono tabular-nums text-gray-700">
                        {t.minutosSomaApontamentosBanco.toLocaleString('pt-BR')}
                      </td>
                      <td className={`px-3 py-2 text-right font-mono text-sm font-medium tabular-nums ${dCls}`}>
                        {dTxt}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div>
        <p className="text-sm font-medium text-gray-800">
          <span className="text-gray-700">{nomeColaborador}</span> — cota por apontamento
        </p>
        <p className="mt-1 text-xs text-gray-500">
          Abaixo: cota de minutos <strong>após</strong> a redistribuição do total (por pessoa e por apontamento), para
          confrontar com o resumo do colaborador.
        </p>
        {itens.length === 0 ? (
          <p className="mt-2 text-sm text-amber-800">Nenhum apontamento retornado para este colaborador.</p>
        ) : (
          <div className="mt-2 overflow-x-auto rounded-lg border border-gray-200">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-left text-xs text-gray-600">
                <tr>
                  <th className="px-3 py-2 font-medium">Id apont.</th>
                  <th className="px-3 py-2 font-medium">Id ticket</th>
                  <th className="px-3 py-2 font-medium">Número</th>
                  <th className="px-3 py-2 text-right font-medium">Min. cota (pós reparte)</th>
                  <th className="px-3 py-2 text-right font-medium">Min. base</th>
                  <th className="px-3 py-2 text-right font-medium">Dif.</th>
                </tr>
              </thead>
              <tbody>
                {itens.map((a) => {
                  const difA = a.minutosPlanilhaCota - a.minutosBase
                  const difTxt = `${difA >= 0 ? '+' : '-'}${Math.abs(difA).toLocaleString('pt-BR')}`
                  const difCorClass =
                    difA === 0
                      ? 'text-gray-500'
                      : difA > 0
                        ? 'text-amber-800'
                        : 'text-blue-800'
                  return (
                    <tr key={a.idClockifyTimeEntry} className="border-t border-gray-200">
                      <td className="px-3 py-2 font-mono text-gray-800">{a.idClockifyTimeEntry}</td>
                      <td className="px-3 py-2 font-mono text-gray-800">
                        {a.idTicket != null ? a.idTicket : '—'}
                      </td>
                      <td className="px-3 py-2 text-gray-800">
                        {a.numeroTicket?.trim() ? a.numeroTicket : '—'}
                      </td>
                      <td className="px-3 py-2 text-right font-mono tabular-nums text-gray-900">
                        {a.minutosPlanilhaCota.toLocaleString('pt-BR')}
                      </td>
                      <td className="px-3 py-2 text-right font-mono tabular-nums text-gray-700">
                        {a.minutosBase.toLocaleString('pt-BR')}
                      </td>
                      <td
                        className={`px-3 py-2 text-right font-mono text-sm font-medium tabular-nums ${difCorClass}`}
                      >
                        {difTxt}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}

export default function UploadPlanilhaSuporte() {
  const toast = useToast()

  const [dataEnvioMomentum, setDataEnvioMomentum] = useState('')
  const [dataAprovacao, setDataAprovacao] = useState('')
  const [dataFaturamento, setDataFaturamento] = useState('')
  const [quinzena, setQuinzena] = useState<'1' | '2' | ''>(() => {
    const hoje = new Date()
    const dia = hoje.getDate()
    return dia >= 16 ? '1' : '2'
  })
  const [quinzenaAlteradaManualmente, setQuinzenaAlteradaManualmente] = useState(false)
  const [arquivo, setArquivo] = useState<File | null>(null)
  /** Carregando pré-visualização após clicar em Gerar faturamento */
  const [carregandoPreview, setCarregandoPreview] = useState(false)
  /** Enviando planilha após Confirmar na modal */
  const [enviandoFaturamento, setEnviandoFaturamento] = useState(false)
  const [preview, setPreview] = useState<PlanilhaSuportePreviewDto | null>(null)
  const [resumo, setResumo] = useState<{
    idFaturamento: number
    valorTotal: number
    quantidadeTickets: number
    totalMinutosPlanilha?: number | null
    taxaHoraAplicada?: number | null
    idValorHoraReferencia?: number | null
  } | null>(null)

  const atualizarQuinzenaPorData = (novaData: string) => {
    if (!novaData || quinzenaAlteradaManualmente) return
    const partes = novaData.split('/')
    if (partes.length < 1) return
    const dia = Number(partes[0])
    if (!Number.isFinite(dia) || dia <= 0) return
    if (dia >= 16) setQuinzena('1')
    else setQuinzena('2')
  }

  const formularioCompleto =
    Boolean(dataEnvioMomentum && dataAprovacao && dataFaturamento && quinzena && arquivo)

  const [colaboradorDetalhe, setColaboradorDetalhe] = useState<{
    pessoaId: number
    nome: string
  } | null>(null)

  const fecharModalConferencia = () => {
    if (enviandoFaturamento)
      return
    setColaboradorDetalhe(null)
    setPreview(null)
  }

  const onGerarFaturamento = async () => {
    if (!dataEnvioMomentum || !dataAprovacao || !dataFaturamento || !quinzena || !arquivo) {
      toast.error('Validação', 'Todos os campos são obrigatórios, incluindo o arquivo.')
      return
    }

    setCarregandoPreview(true)
    setResumo(null)
    setPreview(null)
    try {
      const dados = await faturamentoService.previewPlanilhaSuporte({
        dataEnvioMomentum,
        dataAprovacao,
        dataFaturamento,
        quinzena: quinzena === '1' ? 1 : 2,
        arquivo,
      })
      setPreview(dados)
    } catch (error: unknown) {
      const msg = error instanceof Error ? error.message : 'Erro ao pré-visualizar planilha'
      toast.error('Erro', msg)
    } finally {
      setCarregandoPreview(false)
    }
  }

  const onConfirmarGeracaoFaturamento = async () => {
    if (!dataEnvioMomentum || !dataAprovacao || !dataFaturamento || !quinzena || !arquivo) {
      toast.error('Validação', 'Dados incompletos. Preencha o formulário novamente.')
      return
    }

    setEnviandoFaturamento(true)
    try {
      const faturamento = await faturamentoService.uploadPlanilhaSuporte({
        dataEnvioMomentum,
        dataAprovacao,
        dataFaturamento,
        quinzena: quinzena === '1' ? 1 : 2,
        arquivo,
      })
      toast.success('Sucesso', 'Faturamento de suporte gerado com sucesso.')
      setPreview(null)
      setResumo({
        idFaturamento: faturamento.idFaturamento,
        valorTotal: faturamento.valorTotal,
        quantidadeTickets: faturamento.ticketsNf ? faturamento.ticketsNf.length : 0,
        totalMinutosPlanilha: faturamento.totalMinutosPlanilha ?? null,
        taxaHoraAplicada: faturamento.taxaHoraAplicada ?? null,
        idValorHoraReferencia: faturamento.idValorHoraReferencia ?? null,
      })
      setArquivo(null)
      setQuinzena('')
      setDataEnvioMomentum('')
      setDataAprovacao('')
      setDataFaturamento('')
    } catch (error: unknown) {
      const msg = error instanceof Error ? error.message : 'Erro ao processar planilha de suporte'
      toast.error('Erro', msg)
    } finally {
      setEnviandoFaturamento(false)
    }
  }

  return (
    <div className="space-y-6 max-w-4xl">
      <div className="flex items-start gap-3">
        <div className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-brand-50 text-brand-600">
          <FileSpreadsheet className="h-5 w-5" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Upload de Planilha de Suporte</h1>
          <p className="mt-1 text-sm text-gray-600">
            Informe as datas, a quinzena e o arquivo. Ao clicar em Gerar faturamento, abrimos a conferência da planilha; confirme para gravar ou cancele.
          </p>
        </div>
      </div>

      <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-theme-sm space-y-6">
        {/* Linha 1 - Quinzena */}
        <div>
          <span className="block text-sm font-medium text-gray-700 mb-2">
            Quinzena <span className="text-red-500">*</span>
          </span>
          <div className="inline-flex rounded-lg border border-gray-200 bg-gray-50 p-1">
            <button
              type="button"
              onClick={() => {
                setQuinzena('1')
                setQuinzenaAlteradaManualmente(true)
              }}
              className={`px-3 py-1.5 text-xs font-medium rounded-md ${
                quinzena === '1'
                  ? 'bg-brand-500 text-white shadow-theme-xs'
                  : 'text-gray-700 hover:bg-white'
              }`}
            >
              1ª Quinzena (16 ao último dia)
            </button>
            <button
              type="button"
              onClick={() => {
                setQuinzena('2')
                setQuinzenaAlteradaManualmente(true)
              }}
              className={`ml-1 px-3 py-1.5 text-xs font-medium rounded-md ${
                quinzena === '2'
                  ? 'bg-brand-500 text-white shadow-theme-xs'
                  : 'text-gray-700 hover:bg-white'
              }`}
            >
              2ª Quinzena (01 a 15)
            </button>
          </div>
        </div>

        {/* Linha 2 - Datas (compactas) */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="max-w-xs">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Data Envio Momentum <span className="text-red-500">*</span>
            </label>
            <InputDate
              value={dataEnvioMomentum}
              onChange={(valor) => {
                setDataEnvioMomentum(valor)
                atualizarQuinzenaPorData(valor)
              }}
              placeholder="dd/mm/aaaa"
              className="w-full"
            />
          </div>
          <div className="max-w-xs">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Data Aprovação <span className="text-red-500">*</span>
            </label>
            <InputDate
              value={dataAprovacao}
              onChange={setDataAprovacao}
              placeholder="dd/mm/aaaa"
              className="w-full"
            />
          </div>
          <div className="max-w-xs">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Data Faturamento <span className="text-red-500">*</span>
            </label>
            <InputDate
              value={dataFaturamento}
              onChange={setDataFaturamento}
              placeholder="dd/mm/aaaa"
              className="w-full"
            />
          </div>
        </div>

        {/* Linha 3 - Componente de arquivo estilo documentos */}
        <div>
          <FileUpload
            accept=".xlsx"
            selectedFile={arquivo}
            onFileSelect={(file) => setArquivo(file)}
            label="Arquivo da planilha (.xlsx) *"
            className="w-full"
            maxSizeMB={10}
          />
          <p className="mt-1 text-xs text-gray-500">
            Utilize a planilha de geração para envio com todas as colunas preenchidas, incluindo IdTicket, Nota Fiscal e Aprovado (S/N).
          </p>
        </div>

        <div className="flex flex-wrap justify-end gap-3 pt-2">
          <button
            type="button"
            onClick={onGerarFaturamento}
            disabled={carregandoPreview || enviandoFaturamento || !formularioCompleto}
            className="inline-flex items-center px-5 py-2.5 rounded-lg bg-brand-500 text-white text-sm font-medium hover:bg-brand-600 disabled:opacity-50 shadow-theme-sm"
          >
            {carregandoPreview ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <UploadCloud className="w-4 h-4 mr-2" />
            )}
            Gerar faturamento
          </button>
        </div>

        {resumo && (
          <div className="mt-6 border-t border-gray-200 pt-4">
            <h2 className="text-sm font-semibold text-gray-900 mb-3">Resumo do faturamento gerado</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 text-sm">
              <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
                <p className="text-xs text-gray-500">Faturamento ID</p>
                <p className="mt-1 font-semibold text-gray-900">{resumo.idFaturamento}</p>
              </div>
              <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
                <p className="text-xs text-gray-500">Valor total</p>
                <p className="mt-1 font-semibold text-gray-900">R$ {resumo.valorTotal.toFixed(2)}</p>
              </div>
              <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
                <p className="text-xs text-gray-500">Total minutos (planilha)</p>
                <p className="mt-1 font-semibold text-gray-900">
                  {resumo.totalMinutosPlanilha != null ? resumo.totalMinutosPlanilha.toLocaleString('pt-BR') : '—'}
                </p>
              </div>
              <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
                <p className="text-xs text-gray-500">Taxa hora aplicada</p>
                <p className="mt-1 font-semibold text-gray-900">
                  {resumo.taxaHoraAplicada != null ? `R$ ${resumo.taxaHoraAplicada.toFixed(2)}` : '—'}
                </p>
              </div>
              <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
                <p className="text-xs text-gray-500">ID vigência valor hora</p>
                <p className="mt-1 font-semibold text-gray-900">
                  {resumo.idValorHoraReferencia != null ? resumo.idValorHoraReferencia : '—'}
                </p>
              </div>
              <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
                <p className="text-xs text-gray-500">Quantidade de tickets</p>
                <p className="mt-1 font-semibold text-gray-900">{resumo.quantidadeTickets}</p>
              </div>
            </div>
          </div>
        )}
      </div>

      <Modal
        isOpen={preview !== null}
        onClose={fecharModalConferencia}
        title="Conferência da planilha"
        subtitle="Revise totais, taxa e alertas. Nada foi gravado ainda — confirme para gerar o faturamento ou cancele."
        size="4xl"
        icon={
          <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-brand-50 text-brand-600 min-h-[2.5rem] min-w-[2.5rem]">
            <ClipboardList className="h-5 w-5" />
          </div>
        }
        footer={
          <div className="flex flex-wrap justify-end gap-3 px-6 py-4">
            <button
              type="button"
              onClick={fecharModalConferencia}
              disabled={enviandoFaturamento}
              className="inline-flex items-center rounded-lg border border-gray-300 bg-white px-5 py-2.5 text-sm font-medium text-gray-800 shadow-theme-sm hover:bg-gray-50 disabled:opacity-50"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={onConfirmarGeracaoFaturamento}
              disabled={enviandoFaturamento}
              className="inline-flex items-center rounded-lg bg-brand-500 px-5 py-2.5 text-sm font-medium text-white shadow-theme-sm hover:bg-brand-600 disabled:opacity-50"
            >
              {enviandoFaturamento ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <UploadCloud className="mr-2 h-4 w-4" />
              )}
              Confirmar e gerar
            </button>
          </div>
        }
      >
        {preview && (
          <div className="space-y-6">
            {preview.alertas.length > 0 && (
              <div
                className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950"
                role="alert"
              >
                <p className="font-medium text-amber-900">Alertas</p>
                <ul className="mt-2 list-disc space-y-1 pl-5">
                  {preview.alertas.map((a, i) => (
                    <li key={i}>{a}</li>
                  ))}
                </ul>
              </div>
            )}

            <div className="grid grid-cols-1 gap-4 text-sm sm:grid-cols-2 lg:grid-cols-4">
              <div className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 shadow-theme-sm">
                <p className="text-xs text-gray-500">Minutos (planilha)</p>
                <p className="mt-1 font-semibold text-gray-900">
                  {preview.totalMinutosPlanilha.toLocaleString('pt-BR')}
                </p>
              </div>
              <div className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 shadow-theme-sm">
                <p className="text-xs text-gray-500">Minutos Clockify (bruto)</p>
                <p className="mt-1 font-semibold text-gray-900">
                  {preview.totalMinutosApontamentosBrutos.toLocaleString('pt-BR')}
                </p>
              </div>
              <div className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 shadow-theme-sm">
                <p className="text-xs text-gray-500">Δ bruto vs planilha</p>
                <p className="mt-1 font-semibold text-gray-900">
                  {preview.deltaMinutosBrutosVsPlanilha >= 0 ? '+' : ''}
                  {preview.deltaMinutosBrutosVsPlanilha.toLocaleString('pt-BR')}
                </p>
              </div>
              <div className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 shadow-theme-sm">
                <p className="text-xs text-gray-500">Minutos após redistribuição</p>
                <p className="mt-1 font-semibold text-gray-900">
                  {preview.totalMinutosAposRedistribuicao.toLocaleString('pt-BR')}
                </p>
              </div>
              <div className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 shadow-theme-sm">
                <p className="text-xs text-gray-500">Taxa hora aplicada</p>
                <p className="mt-1 font-semibold text-gray-900">
                  R$ {preview.taxaHoraAplicada.toFixed(2)}
                </p>
              </div>
              <div className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 shadow-theme-sm">
                <p className="text-xs text-gray-500">ID valor hora (vigência)</p>
                <p className="mt-1 font-semibold text-gray-900">{preview.idValorHoraReferencia}</p>
              </div>
              <div className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 shadow-theme-sm">
                <p className="text-xs text-gray-500">Início vigência</p>
                <p className="mt-1 font-semibold text-gray-900">
                  {formatarDataValidade(preview.dataInicioValidadeValorHora)}
                </p>
              </div>
              <div className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 shadow-theme-sm">
                <p className="text-xs text-gray-500">Fim vigência</p>
                <p className="mt-1 font-semibold text-gray-900">
                  {formatarDataValidade(preview.dataFimValidadeValorHora)}
                </p>
              </div>
              <div className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 shadow-theme-sm">
                <p className="text-xs text-gray-500">Valor total projetado</p>
                <p className="mt-1 font-semibold text-gray-900">
                  R$ {preview.valorTotalProjetado.toFixed(2)}
                </p>
              </div>
              <div className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 shadow-theme-sm">
                <p className="text-xs text-gray-500">Linhas / tickets / apontamentos</p>
                <p className="mt-1 font-semibold text-gray-900">
                  {preview.quantidadeLinhasPlanilha} / {preview.quantidadeTicketsDistintos} /{' '}
                  {preview.quantidadeApontamentosSelecionados}
                </p>
              </div>
              <div className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 shadow-theme-sm">
                <p className="text-xs text-gray-500">Linhas sem Id ticket numérico</p>
                <p className="mt-1 font-semibold text-gray-900">
                  {(preview.linhasSemIdTicketNumerico ?? []).length.toLocaleString('pt-BR')}
                </p>
              </div>
              <div className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 shadow-theme-sm">
                <p className="text-xs text-gray-500">Minutos (linhas sem Id válido)</p>
                <p className="mt-1 font-semibold text-gray-900">
                  {(preview.linhasSemIdTicketNumerico ?? []).length === 0
                    ? '—'
                    : (preview.linhasSemIdTicketNumerico ?? [])
                        .reduce((acc, l) => acc + l.minutosLinha, 0)
                        .toLocaleString('pt-BR')}
                </p>
              </div>
            </div>

            {(preview.linhasSemIdTicketNumerico ?? []).length > 0 && (
              <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-theme-sm">
                <div className="border-b border-gray-100 px-4 py-2 text-sm font-medium text-gray-800">
                  Linhas sem Id ticket numérico (fora do faturamento)
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead className="bg-gray-50 text-left text-xs text-gray-600">
                      <tr>
                        <th className="px-4 py-2 font-medium">Linha (Excel)</th>
                        <th className="px-4 py-2 font-medium">Texto na coluna Id</th>
                        <th className="px-4 py-2 font-medium">Nota fiscal</th>
                        <th className="px-4 py-2 font-medium">Minutos</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(preview.linhasSemIdTicketNumerico ?? []).map((linha) => (
                        <tr key={linha.numeroLinhaPlanilha} className="border-t border-gray-100">
                          <td className="px-4 py-2 font-mono text-gray-900">{linha.numeroLinhaPlanilha}</td>
                          <td className="max-w-xs truncate px-4 py-2 text-gray-700" title={linha.trechoIdTicketInformado}>
                            {linha.trechoIdTicketInformado.trim() === '' ? '—' : linha.trechoIdTicketInformado}
                          </td>
                          <td className="px-4 py-2 text-gray-700">{linha.numeroNotaFiscal}</td>
                          <td className="px-4 py-2 font-mono text-gray-900">
                            {linha.minutosLinha.toLocaleString('pt-BR')}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {preview.linhasComProblema.length > 0 && (
              <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-theme-sm">
                <div className="border-b border-gray-100 px-4 py-2 text-sm font-medium text-gray-800">
                  Tickets para conferência rápida
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead className="bg-gray-50 text-left text-xs text-gray-600">
                      <tr>
                        <th className="px-4 py-2 font-medium">Id ticket</th>
                        <th className="px-4 py-2 font-medium">Motivo</th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview.linhasComProblema.map((linha, idx) => (
                        <tr key={`${linha.idTicket}-${idx}`} className="border-t border-gray-100">
                          <td className="px-4 py-2 font-mono">{linha.idTicket}</td>
                          <td className="px-4 py-2 text-gray-700">{linha.motivo}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-theme-sm">
              <div className="border-b border-gray-100 px-4 py-2">
                <p className="text-sm font-medium text-gray-800">Colaboradores (estimativa)</p>
                <p className="mt-0.5 text-xs text-gray-500">
                  Primeiro a cota a partir do total lido da planilha; depois os minutos do cadastro (apontamentos
                  enviados / Clockify no Zeus). A coluna Diferença compara cota planilha − base.                   Quando houver
                  diferença, clique na lupa para abrir a conferência arquivo×banco por ticket e a lista de apontamentos
                  (cota após repartição) deste colaborador.
                </p>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50 text-left text-xs text-gray-600">
                    <tr>
                      <th className="px-4 py-2 font-medium">Nome</th>
                      <th className="px-4 py-2 text-right font-medium" title="Cota proporcional do total de minutos aprovado na planilha">
                        Min. planilha
                      </th>
                      <th className="px-4 py-2 text-right font-medium" title="Soma de apontamentos (Clockify) no cadastro">
                        Min. base
                      </th>
                      <th className="px-4 py-2 text-right font-medium">Diferença</th>
                      <th className="px-4 py-2 text-right font-medium">Valor estimado</th>
                      <th className="w-12 px-2 py-2 text-center" aria-label="Detalhe apontamentos" />
                    </tr>
                  </thead>
                  <tbody>
                    {preview.colaboradores.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="px-4 py-6 text-center text-gray-500">
                          Nenhum colaborador mapeado nesta pré-visualização.
                        </td>
                      </tr>
                    ) : (
                      preview.colaboradores.map((c) => {
                        const difPlanilhaMenosBase =
                          c.minutosAposRedistribuicao - c.minutosBrutosClockify
                        const difFormatada = `${difPlanilhaMenosBase >= 0 ? '+' : '-'}${Math.abs(
                          difPlanilhaMenosBase
                        ).toLocaleString('pt-BR')}`
                        const difCor =
                          difPlanilhaMenosBase === 0
                            ? 'text-gray-500'
                            : difPlanilhaMenosBase > 0
                              ? 'text-amber-800'
                              : 'text-blue-800'
                        const temDiferenca = difPlanilhaMenosBase !== 0
                        return (
                          <tr key={c.pessoaId} className="border-t border-gray-100">
                            <td className="px-4 py-2 text-gray-900">{c.nome}</td>
                            <td className="px-4 py-2 text-right font-mono text-gray-900 tabular-nums">
                              {c.minutosAposRedistribuicao.toLocaleString('pt-BR')}
                            </td>
                            <td className="px-4 py-2 text-right font-mono text-gray-700 tabular-nums">
                              {c.minutosBrutosClockify.toLocaleString('pt-BR')}
                            </td>
                            <td
                              className={`px-4 py-2 text-right font-mono text-sm font-medium tabular-nums ${difCor}`}
                            >
                              {difFormatada}
                            </td>
                            <td className="px-4 py-2 text-right text-gray-900 tabular-nums">
                              R$ {c.valorAlocadoEstimado.toFixed(2)}
                            </td>
                            <td className="px-2 py-2 text-center">
                              {temDiferenca ? (
                                <button
                                  type="button"
                                  onClick={() =>
                                    setColaboradorDetalhe({ pessoaId: c.pessoaId, nome: c.nome })
                                  }
                                  className="inline-flex rounded-lg p-1.5 text-gray-500 hover:bg-gray-100 hover:text-brand-600"
                                  title="Ver apontamentos e tickets deste colaborador"
                                >
                                  <Search className="h-4 w-4" />
                                </button>
                              ) : (
                                <span className="text-gray-300" aria-hidden>
                                  —
                                </span>
                              )}
                            </td>
                          </tr>
                        )
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </Modal>

      <Modal
        isOpen={colaboradorDetalhe !== null}
        onClose={() => setColaboradorDetalhe(null)}
        title={colaboradorDetalhe ? `Detalhe: ${colaboradorDetalhe.nome}` : 'Detalhe'}
        subtitle="Conferência do arquivo de faturamento com o banco, por ticket; e detalhe por apontamento (cota após repartição)."
        size="4xl"
        icon={
          <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-brand-50 text-brand-600 min-h-[2.5rem] min-w-[2.5rem]">
            <Search className="h-5 w-5" />
          </div>
        }
        footer={
          <div className="flex flex-wrap justify-end gap-3 px-6 py-4">
            <button
              type="button"
              onClick={() => setColaboradorDetalhe(null)}
              className="inline-flex items-center rounded-lg border border-gray-300 bg-white px-5 py-2.5 text-sm font-medium text-gray-800 shadow-theme-sm hover:bg-gray-50"
            >
              Fechar
            </button>
          </div>
        }
      >
        {colaboradorDetalhe && preview ? (
          <ConteudoModalColaboradorApontamentos
            nomeColaborador={colaboradorDetalhe.nome}
            conferenciaTickets={(() => {
              const ticketsDoCol = new Set<number>()
              for (const a of preview.apontamentos ?? []) {
                if (a.pessoaId !== colaboradorDetalhe.pessoaId)
                  continue
                if (a.idTicket != null)
                  ticketsDoCol.add(a.idTicket)
              }
              return (preview.conferenciaTickets ?? [])
                .filter((t) => ticketsDoCol.has(t.idTicket))
                .slice()
                .sort((a, b) => a.idTicket - b.idTicket)
            })()}
            itens={(preview.apontamentos ?? [])
              .filter(
                (a) => a.pessoaId != null && a.pessoaId === colaboradorDetalhe.pessoaId
              )
              .slice()
              .sort((a, b) => a.idClockifyTimeEntry - b.idClockifyTimeEntry)}
          />
        ) : null}
      </Modal>
    </div>
  )
}
